import { z } from 'zod';
import {
  SYSTEM_PROMPT_MAX_CHARS,
  agentLanguageSchema,
  agentToneSchema,
} from '@logistics/contracts';
import type { WireAgentSettings } from '@logistics/contracts';

/** The Agent settings form (design L457–506). The API checks it again on save. */
export const agentFormSchema = z.object({
  model: z.string().min(1, 'Pick a model.'),
  language: agentLanguageSchema,
  tone: agentToneSchema,
  systemPrompt: z
    .string()
    .max(
      SYSTEM_PROMPT_MAX_CHARS,
      `The system prompt is at most ${SYSTEM_PROMPT_MAX_CHARS} characters.`,
    ),
});

export type AgentForm = z.input<typeof agentFormSchema>;

/** The form as loaded: no model saved means the deployment's default, the first offered. */
export function agentFormValues(settings: WireAgentSettings, models: readonly string[]): AgentForm {
  return {
    model: settings.model ?? models[0] ?? '',
    language: settings.language,
    tone: settings.tone,
    systemPrompt: settings.systemPrompt,
  };
}

const LANGUAGE_LABELS: Readonly<Record<AgentForm['language'], string>> = {
  auto: 'Match sender',
  en: 'English',
  ru: 'Russian',
  'uz-Latn': 'Uzbek (Latin)',
  'uz-Cyrl': 'Uzbek (Cyrillic)',
};

export function languageLabel(language: AgentForm['language']): string {
  return LANGUAGE_LABELS[language];
}

/** "concise" → "Concise". */
export function toneLabel(tone: string): string {
  return tone.charAt(0).toUpperCase() + tone.slice(1);
}

/** "Last change · Sep 12, 14:20 by Anna Kowalski", or that nobody has changed it. */
export function lastChange(
  settings: Pick<WireAgentSettings, 'updatedAt' | 'updatedBy'>,
  format: (iso: string) => string,
): string {
  if (settings.updatedAt === undefined) return 'Last change · never — deployment defaults';
  const by = settings.updatedBy === undefined ? '' : ` by ${settings.updatedBy.name}`;
  return `Last change · ${format(settings.updatedAt)}${by}`;
}
